const GROUP_TOKEN_KEY = 'dice1k_group_token';
const GROUP_LAST_GAME_KEY = 'dice1k_group_last_game';
const GROUP_PLAYER_KEY = 'dice1k_group_player';

type GroupPlayerRecord = { playerId: string; isHost?: boolean };

function sanitizeGroupPlayerMap(parsed: unknown): Record<string, GroupPlayerRecord> {
  if (!parsed || typeof parsed !== 'object') return {};

  return Object.entries(parsed as Record<string, unknown>).reduce<Record<string, GroupPlayerRecord>>((acc, [token, value]) => {
    if (!value || typeof value !== 'object') return acc;
    const payload = value as Record<string, unknown>;
    const playerId = typeof payload.playerId === 'string' ? payload.playerId.trim() : '';
    if (!playerId) return acc;
    acc[token] = {
      playerId,
      ...(typeof payload.isHost === 'boolean' ? { isHost: payload.isHost } : {}),
    };
    return acc;
  }, {});
}

function readGroupPlayerMap(): Record<string, GroupPlayerRecord> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(GROUP_PLAYER_KEY);
    if (!raw) return {};
    const sanitized = sanitizeGroupPlayerMap(JSON.parse(raw));
    const nextRaw = JSON.stringify(sanitized);
    if (nextRaw !== raw) {
      localStorage.setItem(GROUP_PLAYER_KEY, nextRaw);
    }
    return sanitized;
  } catch {
    return {};
  }
}

export function rememberGroupContext(token: string | null | undefined, lastGameId?: string | null): void {
  if (typeof localStorage === 'undefined') return;
  if (!token) return;
  try {
    localStorage.setItem(GROUP_TOKEN_KEY, token);
    if (lastGameId) {
      localStorage.setItem(GROUP_LAST_GAME_KEY, lastGameId);
    }
  } catch {
    // ignore persistence issues
  }
}

export function loadGroupToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const token = localStorage.getItem(GROUP_TOKEN_KEY);
    return token || null;
  } catch {
    return null;
  }
}

export function loadGroupLastGame(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(GROUP_LAST_GAME_KEY);
  } catch {
    return null;
  }
}

export function rememberGroupPlayer(
  token: string | null | undefined,
  playerId: string,
  options?: { isHost?: boolean }
): void {
  if (typeof localStorage === 'undefined') return;
  if (!token) return;
  if (!playerId) return;
  try {
    const map = readGroupPlayerMap();
    const previous = map[token];
    const nextIsHost = typeof options?.isHost === 'boolean'
      ? options.isHost
      : (previous?.isHost ?? false);
    map[token] = { playerId, isHost: nextIsHost };
    localStorage.setItem(GROUP_PLAYER_KEY, JSON.stringify(map));
  } catch {
    // ignore persistence issues
  }
}

export function loadGroupPlayer(token: string | null | undefined): GroupPlayerRecord | null {
  if (!token) return null;
  const map = readGroupPlayerMap();
  return map[token] || null;
}

export function clearGroupContext(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(GROUP_TOKEN_KEY);
    localStorage.removeItem(GROUP_LAST_GAME_KEY);
  } catch {
    // ignore
  }
}

export function forgetGroupLastGame(gameId?: string | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (!gameId) {
      localStorage.removeItem(GROUP_LAST_GAME_KEY);
      return;
    }
    const current = localStorage.getItem(GROUP_LAST_GAME_KEY);
    if (current?.trim().toUpperCase() === gameId.trim().toUpperCase()) {
      localStorage.removeItem(GROUP_LAST_GAME_KEY);
    }
  } catch {
    // ignore
  }
}
